// ============================================
// FILE 7: /frontend/src/components/whatsapp/ShareOnWhatsAppButton.jsx
// PURPOSE: WhatsApp share button component
// ============================================

import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import WhatsAppSafetyModal from '../modals/WhatsAppSafetyModal';
import { useToast } from '../../hooks/useToast';
import { useNavigate } from 'react-router-dom';
import { whatsappAPI } from '../../api/whatsapp.api';
import { Button } from '../ui/button';
import { Share2, Loader2 } from 'lucide-react';

/**
 * ShareOnWhatsAppButton Component
 * 
 * @param {Object} props
 * @param {string} props.listingId - Listing ID to share
 * @param {'icon' | 'button' | 'text'} props.variant - Button style variant
 * @param {'sm' | 'md' | 'lg'} props.size - Button size
 * @param {string} props.className - Additional CSS classes
 */
export default function ShareOnWhatsAppButton({ 
  listingId, 
  variant = 'button',
  size = 'md',
  className = ''
}) {
  console.log('[ShareOnWhatsApp] 🎨 Rendering button for listing:', listingId);

  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSharing, setIsSharing] = useState(false);
  const [showSafetyModal, setShowSafetyModal] = useState(false);

  // ========================================
  // HANDLE SHARE CLICK
  // ========================================
  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    console.log('[ShareOnWhatsApp] 📤 Share button clicked');

    // Show safety modal first
    console.log('[ShareOnWhatsApp] 🛡️ Showing safety modal');
    setShowSafetyModal(true);
  };

  // ========================================
  // HANDLE PROCEED TO WHATSAPP
  // ========================================
  
  const handleProceedToWhatsApp = async () => {
    console.log('[ShareOnWhatsApp] ✅ User acknowledged safety - proceeding');
    setShowSafetyModal(false);
    setIsSharing(true);

    try {
      // Generate WhatsApp message
      console.log('[ShareOnWhatsApp] 🔵 Generating WhatsApp message...');
      const response = await whatsappAPI.generateShareMessage(listingId);

      console.log('[ShareOnWhatsApp] ✅ Message generated:', {
        trackingId: response.trackingId,
        shareCount: response.shareCount
      });

      // Show success toast
      toast({
        title: 'Ready to share!',
        description: 'Opening WhatsApp...'
      });

      // Open WhatsApp
      window.open(response.whatsappUrl, '_blank');

      console.log('[ShareOnWhatsApp] 📱 WhatsApp opened');

    } catch (error) {
      console.error('[ShareOnWhatsApp] ❌ Share error:', error);
      
      toast({
        variant: 'destructive',
        title: 'Share failed',
        description: error.response?.data?.message || 'Could not generate WhatsApp message. Please try again.'
      });
    } finally {
      setIsSharing(false);
    }
  };
    
  // ========================================
  // RENDER VARIANTS
  // ========================================
  
  if (variant === 'icon') {
    return (
      <>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleShare}
          disabled={isSharing}
          className={`text-xs sm:text-sm ${className}`}
          title="Share on WhatsApp"
        >
          {isSharing ? (
            <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
          ) : (
            <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
          )}
        </Button>

        <WhatsAppSafetyModal
          isOpen={showSafetyModal}
          onClose={() => setShowSafetyModal(false)}
          onProceed={handleProceedToWhatsApp}
          sellerName="Share this listing"
        />
      </>
    );
  }

  // Text only variant
  if (variant === 'text') {
    return (
      <>
        <button
          onClick={handleShare}
          disabled={isSharing}
          className={`text-xs sm:text-sm font-medium text-primary hover:underline disabled:opacity-50 ${className}`}
        >
          {isSharing ? 'Sharing...' : 'Share on WhatsApp'}
        </button>

        <WhatsAppSafetyModal
          isOpen={showSafetyModal}
          onClose={() => setShowSafetyModal(false)}
          onProceed={handleProceedToWhatsApp}
          sellerName="Share this listing"
        />
      </>
    );
  }

  // Full button variant (default)
  const sizeClasses = {
    sm: 'h-8 text-xs px-2',
    md: 'h-9 sm:h-10 text-xs sm:text-sm px-3',
    lg: 'h-10 sm:h-11 text-sm sm:text-base px-4'
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={handleShare}
        disabled={isSharing}
        className={`${sizeClasses[size]} font-medium ${className}`}
      >
        {isSharing ? (
          <>
            <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 animate-spin" />
            Sharing...
          </>
        ) : (
          <>
            <Share2 className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            Share on WhatsApp
          </>
        )}
      </Button>

      <WhatsAppSafetyModal
        isOpen={showSafetyModal}
        onClose={() => setShowSafetyModal(false)}
        onProceed={handleProceedToWhatsApp}
        sellerName="Share this listing"
      />
    </>
  );
}